// Phase 6 — Dedicated mobile cricket flyer (V4 only). Standalone
// design — not a rebrand of the desktop Cricket V3 broadcast
// layout. Aurora gradient background, big stacked "HOME vs AWAY"
// hero, compact one-column player list with skill chips, full-
// width volunteers card, reporting-time ribbon, and brand footer.
// Lives alongside CricketFlyerV3 so desktop V1/V2/V3 renders the
// original; V4 swaps in this component instead.

import React from "react";
import { Star, CalendarDays, Clock, MapPin } from "lucide-react";
import { SKILL_BY_CODE } from "../../data";
import Avatar from "../Avatar";
import {
  playerSkillCodes, TeamCrest, teamLogoSrc,
  formatDateTemplate, formatTimeTemplate, isCardRosterTeam,
  sortPlayersForSport
} from "./flyerHelpers";

export function CricketFlyerMobile({
  match, opp, homeTeamName, homeAcronym, footerBrand,
  team, rules, startingPlayers, reportingTime,
  venueShort, venueAddress,
  showVolunteers, snacksVolunteer, alcoholVolunteer,
  template = null
}) {
  // League / tournament subheading. A per-team Tournament Name (set
  // on the Team edit form) wins and is uppercased for the mobile
  // flyer's all-caps aesthetic. Otherwise fall back to the hard-coded
  // defaults — Rowdys + T100 → "Pittsburgh Cricket League", everyone
  // else → "Pittsburgh Premier Cricket League". The Rowdys template
  // (used to borrow the colour palette) is still forced to default.
  const teamTournament = (team?.tournamentName || "").trim();
  const isRowdys = team?.code === "ROWDYS_CRICKET";
  const defaultLeagueLine = teamTournament
    ? teamTournament.toUpperCase()
    : (isRowdys || team?.code === "PITT_GABRUZ_T100"
        ? "PITTSBURGH CRICKET LEAGUE"
        : "PITTSBURGH PREMIER CRICKET LEAGUE");
  const leagueLine = isRowdys
    ? defaultLeagueLine
    : (template?.leagueLine || defaultLeagueLine);
  const showSkills    = template?.showSkills !== false;
  const useVolunteers = showVolunteers && (template?.showVolunteers !== false);

  // For Rowdys, Squads.jsx swaps in Pitt Gabruz's flyer template so
  // the colour combination matches Pitt Gabruz's flyer exactly. This
  // component just applies whatever template it receives.
  const primaryColor = template?.primaryColor || null;
  const bgTone       = template?.bgTone       || null;

  const rootStyle = {};
  if (primaryColor) rootStyle["--cfm-gold"] = primaryColor;
  if (bgTone === "darker")  rootStyle["--cfm-bg-deep"] = "#04060e";
  if (bgTone === "lighter") rootStyle["--cfm-bg-deep"] = "#121b34";

  return (
    <div className="poster poster--cricketMobile" style={rootStyle}>
      {/* Decorative team logo as a low-opacity background watermark.
          Captured by html2canvas so it appears in the downloaded PNG. */}
      <img
        className="cfm-watermark"
        src={teamLogoSrc(team)}
        alt=""
        aria-hidden="true"
        crossOrigin="anonymous"
        onError={e => e.currentTarget.style.display = "none"}
      />

      {/* ── HEADER ─────────────────────────────────────────── */}
      <header className="cfm-header">
        <div className="cfm-stars">
          <Star size={11} fill="currentColor" stroke="none"/>
          <Star size={15} fill="currentColor" stroke="none"/>
          <Star size={11} fill="currentColor" stroke="none"/>
        </div>
        <h1 className="cfm-brand">GABRUZ UNIVERSE</h1>
        <div className="cfm-leaguePill">
          <span className="cfm-leagueChev">»</span>
          {leagueLine}
          <span className="cfm-leagueChev">«</span>
        </div>
      </header>

      {/* ── MATCH HERO ──────────────────────────────────────
          Layout:
            row 1 — HOME team |  LOGO  | AWAY team
            row 2 —              VS  (centered)
            row 3 — Match Day | Reporting | Ground chips (3 cols) */}
      <article className="cfm-matchCard">
        <div className="cfm-matchCard-hero">
          <div className="cfm-matchCard-team cfm-matchCard-team--home">
            <small>HOME</small>
            <b>{homeTeamName}</b>
          </div>
          <div className="cfm-matchCard-logoRing">
            <TeamCrest team={team} fallbackName={homeAcronym} className="cfm-matchCard-logo"/>
          </div>
          <div className="cfm-matchCard-team cfm-matchCard-team--away">
            <small>AWAY</small>
            <b>{opp}</b>
          </div>
        </div>

        <div className="cfm-matchCard-vsRow">
          <span className="cfm-matchCard-vsLabel">VS</span>
        </div>

        <div className="cfm-matchCard-chips">
          <div className="cfm-matchCard-chip">
            <CalendarDays size={14}/>
            <div>
              <small>MATCH DAY</small>
              <b>{formatDateTemplate(match?.date)}</b>
            </div>
          </div>
          <div className="cfm-matchCard-chip">
            <Clock size={14}/>
            <div>
              <small>REPORTING</small>
              <b>{formatTimeTemplate(reportingTime)}</b>
            </div>
          </div>
          <div className="cfm-matchCard-chip">
            <MapPin size={14}/>
            <div>
              <small>GROUND</small>
              <b>{venueShort}</b>
              {venueAddress && <em>{venueAddress}</em>}
            </div>
          </div>
        </div>
      </article>

      {/* ── SQUAD ─────────────────────────────────────────── */}
      {/* Iterate the actual startingPlayers array — no empty
          placeholder rows when the squad has fewer than the max.
          777-format teams render the same alphabetised member-card
          list used on the desktop flyer instead of the numbered rows. */}
      <section className="cfm-squad">
        {isCardRosterTeam(team) ? (
          <div className="cfm-cardList">
            {sortPlayersForSport(startingPlayers, team, "Cricket")
              .map((p, i) => {
                const codes = playerSkillCodes(team, p.id);
                return (
                  <article key={p.id || i} className="cfm-memberCard">
                    <div className="cfm-memberCard-avatar">
                      {p.id ? (
                        <Avatar
                          member={{ id: p.id, name: p.name, avatarUpdatedAt: p.avatarUpdatedAt }}
                          size={48}
                          style={{ width: 48, height: 48, borderRadius: 12, background: "transparent" }}
                        />
                      ) : (
                        <span className="cfm-memberCard-initials">
                          {(p.name || "?").split(/\s+/).slice(0, 2).map(s => s[0] || "").join("").toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="cfm-memberCard-body">
                      <div className="cfm-memberCard-name">{p.name}</div>
                      {showSkills && codes.length > 0 && (
                        <div className="cfm-memberCard-roles">
                          {codes.slice(0, 3).map(c => {
                            const s = SKILL_BY_CODE[c];
                            return (
                              <span
                                key={c}
                                className={`cfm-memberCard-skill cfm-memberCard-skill--${s?.category || "OTHER"}`}
                                title={s?.label}
                              >{s?.short || c}</span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
          </div>
        ) : (
          <ol className="cfm-squad-list">
            {sortPlayersForSport(startingPlayers, team, "Cricket").map((p, i) => {
              const codes = playerSkillCodes(team, p.id);
              return (
                <li key={p.id || i} className="cfm-squad-row">
                  <span className="cfm-squad-rowNum">{String(i + 1).padStart(2, "0")}</span>
                  <span className="cfm-squad-rowName">{p.name}</span>
                  {showSkills && codes.length > 0 && (
                    <span className="cfm-squad-rowSkills">
                      {codes.slice(0, 3).map(c => {
                        const skill = SKILL_BY_CODE[c];
                        return (
                          <em
                            key={c}
                            className={`cfm-squad-skill cfm-squad-skill--${skill?.category || "OTHER"}`}
                            title={skill?.label}
                          >{skill?.short || c}</em>
                        );
                      })}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* ── VOLUNTEERS ──────────────────────────────────────
          The "VOLUNTEERS" header label is intentionally omitted;
          the two slots speak for themselves. */}
      {useVolunteers && (
        <section className="cfm-volunteers">
          <div className="cfm-volunteers-grid">
            <div className="cfm-volunteers-slot">
              <small>WATER / GATORADE</small>
              <b>{snacksVolunteer || "— TBD —"}</b>
            </div>
            <div className="cfm-volunteers-slot">
              <small>BANANAS / PROTEIN</small>
              <b>{alcoholVolunteer || "— TBD —"}</b>
            </div>
          </div>
        </section>
      )}

      {/* ── REPORTING STRIP ──────────────────────────────── */}
      <div className="cfm-report">
        <Clock size={20} className="cfm-report-icon"/>
        <div>
          <small>BE AT THE GROUND</small>
          <b>NO LATER THAN {formatTimeTemplate(reportingTime)}</b>
        </div>
      </div>
    </div>
  );
}
